// Публичный API: RPC с конвертом {ok, data | error, message}.
import { z } from 'zod';
import { supabase } from '../supabase';
import { ApiError, toApiError } from '../errors';
import { bookingViewSchema, publicTenantSchema, slotSchema } from './schemas';

async function callEnvelope<T extends z.ZodType>(fn: string, args: Record<string, unknown>, schema: T) {
  let data: unknown;
  try {
    const res = await supabase().rpc(fn, args);
    if (res.error) throw res.error;
    data = res.data;
  } catch (e) {
    throw toApiError(e);
  }
  const env = z
    .object({ ok: z.boolean(), data: z.unknown().optional(), error: z.string().optional(), message: z.string().optional(), token: z.string().optional(), replayed: z.boolean().optional() })
    .parse(data);
  if (!env.ok) throw new ApiError(env.error ?? 'unknown', env.message);
  return { data: schema.parse(env.data) as z.infer<T>, token: env.token, replayed: env.replayed };
}

export const publicApi = {
  tenant: (slug: string) => callEnvelope('get_public_tenant', { p_slug: slug }, publicTenantSchema).then((r) => r.data),

  slots: (slug: string, serviceId: string, from: string, days: number) =>
    callEnvelope('get_slots', { p_slug: slug, p_service_id: serviceId, p_from: from, p_days: days }, z.array(slotSchema)).then(
      (r) => r.data,
    ),

  createBooking: async (input: {
    slug: string;
    serviceId: string;
    startsAt: string;
    name: string;
    phone: string;
    car: string;
    comment: string;
    idempotencyKey: string;
  }) => {
    const r = await callEnvelope(
      'create_booking',
      {
        p_slug: input.slug,
        p_service_id: input.serviceId,
        p_starts_at: input.startsAt,
        p_name: input.name,
        p_phone: input.phone,
        p_car: input.car,
        p_comment: input.comment,
        p_idempotency_key: input.idempotencyKey,
      },
      bookingViewSchema,
    );
    if (!r.token) throw new ApiError('unknown');
    return { booking: r.data, token: r.token, replayed: r.replayed ?? false };
  },

  booking: (token: string) => callEnvelope('get_booking', { p_token: token }, bookingViewSchema).then((r) => r.data),

  cancel: (token: string, reason: string) =>
    callEnvelope('cancel_booking', { p_token: token, p_reason: reason }, bookingViewSchema).then((r) => r.data),

  reschedule: (token: string, startsAt: string) =>
    callEnvelope('reschedule_booking', { p_token: token, p_starts_at: startsAt }, bookingViewSchema).then((r) => r.data),

  registerPush: (token: string, sub: { endpoint: string; p256dh: string; auth: string }) =>
    callEnvelope(
      'register_client_push',
      { p_token: token, p_endpoint: sub.endpoint, p_p256dh: sub.p256dh, p_auth: sub.auth },
      z.object({ reminder_due: z.string().nullable() }),
    ).then((r) => r.data),
};
